import { Popover } from "frost-ui-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useOutsideAlerter from "../hooks/useOutsideAlerter";
import { KeyValue, SectionListKeyValue } from "../models/KeyValue";
import AppIcon from "./Core/AppIcon/AppIcon";
import Checkbox from "./Core/Checkbox/Checkbox";

interface IMultipleSelectPopoverProps {
	label: string;
	onSelect: (key: string | number) => void;
	options: KeyValue[];
	selectedValues: string[] | number[];
	className?: string;
	disabled?: boolean;
	placeholder?: string;
	popoverWidth?: number;

	/**
	 * If popover should be aligned left (default) or right
	 */
	popoverPosition?: "left" | "right";
	sectionList?: boolean;
	sectionListOption?: SectionListKeyValue[];
	/**
	 * Optional counts to display next to each option (e.g., "Status (5)")
	 * Key should match the option key
	 */
	counts?: Record<string | number, number>;
}

export function MultipleSelectPopover({
	label,
	options,
	onSelect,
	className,
	selectedValues,
	disabled,
	popoverWidth = 350,
	popoverPosition = "left",
	placeholder = label,
	sectionList = false,
	sectionListOption,
	counts,
}: IMultipleSelectPopoverProps) {
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	useOutsideAlerter(wrapperRef, close, "mousedown", [close]);

	function close() {
		setOpen(false);
	}

	function handleSelect(key: string | number) {
		onSelect(key);
		// close();
	}

	function handleToggleAll() {
		// Get all available keys from options or section list
		const allKeys: (string | number)[] = sectionList
			? sectionListOption?.flatMap((section) => section.value?.map((item) => item.key) || []) || []
			: options.map((o) => o.key);

		// Check if all items are selected
		const allSelected = allKeys.every((key) =>
			(selectedValues as (string | number)[]).includes(key)
		);

		// If all selected, deselect all. Otherwise, select all.
		allKeys.forEach((key) => {
			const isSelected = (selectedValues as (string | number)[]).includes(key);
			if (allSelected && isSelected) {
				onSelect(key); // Deselect
			} else if (!allSelected && !isSelected) {
				onSelect(key); // Select
			}
		});
	}

	function keyExtractor(o: KeyValue) {
		return JSON.stringify(o);
	}

	function listKeyExtractor(o: SectionListKeyValue) {
		return JSON.stringify(o);
	}

	// Calculate if all items are selected
	const allKeys: (string | number)[] = sectionList
		? sectionListOption?.flatMap((section) => section.value?.map((item) => item.key) || []) || []
		: options.map((o) => o.key);
	const allSelected =
		allKeys.length > 0 &&
		allKeys.every((key) => (selectedValues as (string | number)[]).includes(key));

	return (
		<div
			ref={wrapperRef}
			className={`relative ${className || ""}`}
			style={{ pointerEvents: disabled ? "none" : "auto", opacity: disabled ? 0.6 : 1 }}
		>
			<div
				className="cursor-pointer flex flex-row items-center justify-between select-none"
				onClick={() => setOpen(!open)}
			>
				<div className="flex gap-1.5">{placeholder}</div>
				<AppIcon icon="dropdown" size="12px" />
			</div>

			{open && (
				<div
					className={`absolute top-8 z-[2] ${popoverPosition === "left" ? "left-0" : "right-0"}`}
				>
					<Popover
						pointTo="top"
						styles={{
							padding: "15px 8px",
							width: popoverWidth,
							offset: popoverPosition === "left" ? undefined : String(popoverWidth - 50) + "px",
						}}
					>
						{!sectionList
							? options.map((o, i) => {
									const count = counts?.[o.key];
									const labelWithCount =
										count !== undefined ? `${t(o.value as any)} (${count})` : t(o.value as any);
									return (
										<div
											key={keyExtractor(o)}
											className="my-3 first:mt-0 last:mb-0 flex items-center"
										>
											<Checkbox
												label={labelWithCount}
												// @ts-ignore
												isChecked={selectedValues.includes(o.key) ? true : false}
												onChange={() => handleSelect(o.key)}
												customStyle={true}
											/>
										</div>
									);
							  })
							: Array.isArray(sectionListOption) &&
							  sectionListOption.map((o, i) => (
									<div key={listKeyExtractor(o)} className="mb-3">
										<div className="cursor-default my-3 first:mt-0 flex items-center">
											<h1 className="font-sans text-base font-semibold text-[rgba(79,82,83,1)]">
												{o.header}
											</h1>
										</div>
										<div className="-mt-3 ml-2">
											{Array.isArray(o.value) &&
												o.value.map((val, index) => {
													const count = counts?.[val.key];
													const labelWithCount =
														count !== undefined
															? `${t(val.value as any)} (${count})`
															: t(val.value as any);
													return (
														<div
															key={index}
															className="my-3 first:mt-0 last:mb-0 flex items-center"
														>
															<Checkbox
																label={labelWithCount}
																// @ts-ignore
																isChecked={selectedValues.includes(val.key) ? true : false}
																onChange={() => handleSelect(val.key)}
																customStyle={true}
															/>
														</div>
													);
												})}
										</div>
									</div>
							  ))}

						<button
							onClick={handleToggleAll}
							className="bg-transparent border-0 text-black underline text-[13px] p-0 pr-2 mb-0 cursor-pointer text-left w-full transition-opacity duration-200 hover:opacity-70"
						>
							{allSelected ? t("DESELECT_ALL") : t("SELECT_ALL")}
						</button>
					</Popover>
				</div>
			)}
		</div>
	);
}

export default MultipleSelectPopover;
